import * as React from "react";
import { OraclePublicCloudAuthenticatorSingleton } from "duplo-authentication";
import SendLogs from "../CourierService/SendLogs";
import SendMetrics from "../CourierService/SendMetrics";

const REALM = "default";
// const baseUrl = "r1.oracleiaas.com";
const baseUrl = "us-ashburn-1.oci.oraclecloud.com"
const DEFAULT_TENANT = "bmc_operator_access";
const DEFAULT_PROVIDER = "ocna-saml";
const phonebookApiUrl = "https://accountadmintool.us-ashburn-1.oci.oraclecloud.com/20200331/phonebookserviceteam?q=";
const PHONEBOOK_AUTH_RESULT_CHANNEL = "pb_viewmore_auth_result";
const PHONEBOOK_CHECK_ACCESS_RESULT_CHANNEL = "pb_phonebook_check_access_result";
const PHONEBOOK_AUTHENTICATED_SESSION_KEY = "pb_duplo_authenticated_at";
// const phonebookApiUrl = "https://accountadmintool.r1.oci.oracleiaas.com/20200331/phonebookserviceteam";


// optional: ensure BOAT is initialized only once per session
let boatInitialized = false;

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
const isLikelyAuthError = (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error || "");
    return /security_token|authenticate|auth|token|session/i.test(errorMessage);
};

export default function PhonebookViewMoreAuthGate() {
    React.useEffect(() => {
        const run = async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const queryReturnUrl = searchParams.get("returnUrl");
            const decodedQueryReturnUrl = queryReturnUrl ? decodeURIComponent(queryReturnUrl) : null;
            const returnUrl = decodedQueryReturnUrl || sessionStorage.getItem("pb_viewmore_returnUrl") || "/";
            const requestedTenant = searchParams.get("tenant") || DEFAULT_TENANT;
            const requestedProvider = searchParams.get("provider") || DEFAULT_PROVIDER;
            const mode = searchParams.get("mode");
            const selectedPhonebookValue = searchParams.get("phonebook") || "";
            const requestId = searchParams.get("requestId") || "";
            const userSession = sessionStorage.getItem("user_session") || "phonebook-view-more";

            sessionStorage.setItem("pb_viewmore_returnUrl", returnUrl);
            // Session storage is tab-scoped. When this route is opened in a new tab,
            // the auth-in-progress marker must be set again in this tab so index.tsx
            // can route '/#id_token=...' callbacks back to this auth gate.
            sessionStorage.setItem("pb_auth_in_progress", "true");
            // Do not prefill cloud account name from requested tenant before auth completes.
            // This prevents stale/default Duplo tenant values from leaking back to the form
            // when users cancel auth and navigate back.
            sessionStorage.removeItem("pb_duplo_tenant_prefill");

            // Open-redirect guard (recommended): only allow same-app paths
            const safeReturnUrl = returnUrl.startsWith("/") ? returnUrl : "/";

            const completePopupFlow = (
                payload: Record<string, unknown>,
                channel: string = PHONEBOOK_AUTH_RESULT_CHANNEL
            ) => {
                try {
                    window.localStorage.setItem(
                        channel,
                        JSON.stringify({
                            ...payload,
                            timestamp: Date.now(),
                        })
                    );
                } catch (storageError) {
                    console.warn("Unable to write popup auth result to localStorage", storageError);
                }

                const hasParentFrame = window.parent && window.parent !== window;
                if (hasParentFrame) {
                    try {
                        const parentWindow = window.parent as Window;
                        parentWindow.postMessage({
                            ...payload,
                        }, window.location.origin);
                        return;
                    } catch (postMessageError) {
                        console.warn("Unable to post auth result to parent", postMessageError);
                    }
                }

                const hasOpener = !!window.opener && !window.opener.closed;
                if (hasOpener) {
                    try {
                        const openerWindow = window.opener as Window;
                        openerWindow.postMessage({
                            ...payload,
                        }, window.location.origin);
                    } catch (postMessageError) {
                        console.warn("Unable to post auth result to opener", postMessageError);
                    }

                    try {
                        window.close();
                        return;
                    } catch (closeError) {
                        console.warn("Unable to close auth popup", closeError);
                    }
                }

                window.location.assign(safeReturnUrl);
            };

            // Keep the Duplo callback on the popup auth-gate route itself so the
            // popup does not briefly render the main activation page on '/'.
            const landingUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

            try {
                if (!boatInitialized) {
                    await OraclePublicCloudAuthenticatorSingleton.initialize(
                        // success callback
                        (path) => {
                                                        const restorePath = typeof path === "string" ? path : "";
                            const targetPath = restorePath.startsWith("/") ? restorePath : safeReturnUrl;
                            window.location.assign(targetPath);
                        },
                        // error callback
                        () => undefined,
                        [
                            {
                                realm: REALM,
                                authorizationServiceUrl: () => `https://auth.${baseUrl}/v1`,
                                landingUrl, // IMPORTANT: come back to the same page
                                identityServiceUrl: `https://identity.${baseUrl}/20160918`,
                                loginServiceUrl: `https://login.${baseUrl}/v1`,
                            },
                        ]
                    );
                    boatInitialized = true;
                }

                const authenticator = OraclePublicCloudAuthenticatorSingleton.getInstance(REALM);
                const resolveOverrideTenantName = async () => {
                    try {
                        const overrideTenantName = await (authenticator as any).getOverrideTenantName?.();
                        if (overrideTenantName) {
                            sessionStorage.setItem("pb_duplo_tenant_prefill", overrideTenantName);
                        }
                    } catch (overrideTenantError) {
                        console.warn("Unable to resolve override tenant name", overrideTenantError);
                    }
                };

                const authenticateWithDuplo = async () => {
                    const authenticationError = await authenticator.authenticateUser(
                        undefined,
                        undefined,
                        {
                            tenant: requestedTenant,
                            provider: requestedProvider,
                            noGlobalRedirect: true,
                        }
                    );

                    if (authenticationError) {
                        sessionStorage.removeItem("pb_duplo_tenant_prefill");
                        sessionStorage.removeItem("pb_auth_in_progress");
                        completePopupFlow(
                            {
                                type: mode === "check-access" ? "PB_PHONEBOOK_CHECK_ACCESS_RESULT" : "PB_VIEW_MORE_AUTH_RESULT",
                                requestId,
                                success: false,
                                authenticationError
                            },
                            mode === "check-access" ? PHONEBOOK_CHECK_ACCESS_RESULT_CHANNEL : PHONEBOOK_AUTH_RESULT_CHANNEL
                        );
                        return false;
                    }

                    sessionStorage.setItem(PHONEBOOK_AUTHENTICATED_SESSION_KEY, String(Date.now()));
                    await resolveOverrideTenantName();
                    return true;
                };

                const getSignedFetchApi = async () => {
                    try {
                        return authenticator.getFetchApi();
                    } catch (fetchApiError) {
                        const errorMessage = fetchApiError instanceof Error ? fetchApiError.message : String(fetchApiError || "");
                        if (!/initializeFetchApi/i.test(errorMessage)) {
                            throw fetchApiError;
                        }

                        const requiresAuthentication = await (authenticator as any).setupFetchApi?.();
                        if (requiresAuthentication) {
                            const authenticated = await authenticateWithDuplo();
                            if (!authenticated) {
                                return null;
                            }
                        }

                        return authenticator.getFetchApi();
                    }
                };

                const performCheckAccess = async () => {
                    const signedFetch = await getSignedFetchApi();
                    if (!signedFetch) {
                        return null;
                    }
                    const checkAccessUrl =
                        `https://accountadmintool.us-ashburn-1.oci.oraclecloud.com/20200331/phonebooks/` +
                        `${encodeURIComponent(selectedPhonebookValue)}/actions/checkAccess`;
                    return signedFetch(checkAccessUrl, {
                        method: "POST",
                        headers: {
                            "accept-language": "en"
                        }
                    });
                };

                if (mode === "check-access") {
                    if (!selectedPhonebookValue) {
                        SendMetrics({ name: "PhonebookCheckAccess.Count.Metrics.Error", value: 1, userSession });
                        SendLogs({ severity: 40000, message: "Phonebook checkAccess missing phonebook value", userSession });
                        sessionStorage.removeItem("pb_auth_in_progress");
                        completePopupFlow(
                            {
                                type: "PB_PHONEBOOK_CHECK_ACCESS_RESULT",
                                requestId,
                                success: false,
                                error: "Missing phonebook value"
                            },
                            PHONEBOOK_CHECK_ACCESS_RESULT_CHANNEL
                        );
                        return;
                    }

                    let checkAccessResponse: Response | null;
                    try {
                        checkAccessResponse = await performCheckAccess();
                        if (!checkAccessResponse) {
                            return;
                        }
                        if (checkAccessResponse.status === 401) {
                            const authenticated = await authenticateWithDuplo();
                            if (!authenticated) {
                                return;
                            }
                            checkAccessResponse = await performCheckAccess();
                            if (!checkAccessResponse) {
                                return;
                            }
                        }
                    } catch (checkAccessError) {
                        if (!isLikelyAuthError(checkAccessError)) {
                            throw checkAccessError;
                        }

                        const authenticated = await authenticateWithDuplo();
                        if (!authenticated) {
                            return;
                        }
                        checkAccessResponse = await performCheckAccess();
                        if (!checkAccessResponse) {
                            return;
                        }
                    }

                    let checkAccessResponseBody = "";
                    try {
                        if (typeof (checkAccessResponse as any)?.clone === "function") {
                            checkAccessResponseBody = await checkAccessResponse.clone().text();
                        } else if (typeof (checkAccessResponse as any)?.text === "function") {
                            checkAccessResponseBody = await (checkAccessResponse as any).text();
                        }
                    } catch {}

                    const checkAccessAuthorized =
                        checkAccessResponse.status === 200
                            ? checkAccessResponseBody.trim().toLowerCase() === "true"
                            : checkAccessResponse.ok;

                    if (checkAccessAuthorized) {
                        SendMetrics({ name: "PhonebookCheckAccess.Count.Metrics", value: 1, userSession });
                        SendLogs({
                            severity: 20000,
                            message: `Phonebook checkAccess succeeded for ${selectedPhonebookValue}`,
                            userSession
                        });
                    } else {
                        SendMetrics({ name: "PhonebookCheckAccess.Count.Metrics.Unauthorized", value: 1, userSession });
                        SendLogs({
                            severity: 20000,
                            message: `Phonebook checkAccess unauthorized for ${selectedPhonebookValue}, status: ${checkAccessResponse.status}, body: ${checkAccessResponseBody}`,
                            userSession
                        });
                    }

                    sessionStorage.removeItem("pb_auth_in_progress");
                    completePopupFlow(
                        {
                            type: "PB_PHONEBOOK_CHECK_ACCESS_RESULT",
                            requestId,
                            success: true,
                            ok: checkAccessAuthorized,
                            status: checkAccessResponse.status
                        },
                        PHONEBOOK_CHECK_ACCESS_RESULT_CHANNEL
                    );
                    return;
                }

                const authenticated = await authenticateWithDuplo();
                if (!authenticated) {
                    return;
                }

                const signedFetch = await getSignedFetchApi();
                if (!signedFetch) {
                    return;
                }

                let response: Response | null = null;
                let fetchError: unknown = null;

                for (let attempt = 0; attempt < 3; attempt += 1) {
                    try {
                        response = await signedFetch(phonebookApiUrl, {
                            method: "GET",
                            headers: {
                                "accept-language": "en"
                            }
                        });

                        if (response.ok) {
                            break;
                        }

                        // First popup pass can transiently return unauthorized
                        // right after auth; retry briefly before failing.
                        if ((response.status === 401 || response.status === 403) && attempt < 2) {
                            await wait(500 * (attempt + 1));
                            continue;
                        }

                        break;
                    } catch (error) {
                        fetchError = error;
                        if (attempt < 2) {
                            await wait(500 * (attempt + 1));
                            continue;
                        }
                    }
                }

                if (!response) {
                    throw fetchError instanceof Error
                        ? fetchError
                        : new Error("Phonebook request failed before a response was received");
                }

                const rawResponseText = await response.text();
                                sessionStorage.setItem("pb_viewmore_raw_response", rawResponseText);

                if (!response.ok) {
                    throw new Error(`Phonebook request failed with status ${response.status}`);
                }



                let responseJson: any = null;
                try {
                    responseJson = rawResponseText ? JSON.parse(rawResponseText) : null;
                } catch (parseError) {
                    console.error("Phonebook response was not valid JSON", parseError);
                }

                sessionStorage.setItem("pb_viewmore_response", JSON.stringify(responseJson));
                sessionStorage.removeItem("pb_auth_in_progress");
                sessionStorage.removeItem("pb_viewmore_auth_failed");
                sessionStorage.removeItem("pb_viewmore_auth_error_message");

                completePopupFlow({
                    type: "PB_VIEW_MORE_AUTH_RESULT",
                    success: true,
                    rawResponseText,
                    responseJson,
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("BOAT auth gate failed", e);
                sessionStorage.removeItem("pb_duplo_tenant_prefill");
                sessionStorage.removeItem("pb_auth_in_progress");
                completePopupFlow(
                    {
                        type: mode === "check-access" ? "PB_PHONEBOOK_CHECK_ACCESS_RESULT" : "PB_VIEW_MORE_AUTH_RESULT",
                        requestId,
                        success: false,
                    },
                    mode === "check-access" ? PHONEBOOK_CHECK_ACCESS_RESULT_CHANNEL : PHONEBOOK_AUTH_RESULT_CHANNEL
                );
                if (mode === "check-access") {
                    SendMetrics({ name: "PhonebookCheckAccess.Count.Metrics.Fail", value: 1, userSession });
                    SendLogs({
                        severity: 40000,
                        message: `Phonebook checkAccess failed: ${e instanceof Error ? e.message : "unknown error"}`,
                        userSession
                    });
                }
            }
        };

        run();
    }, []);

    return null;
}
